import React, { useState, useEffect } from 'react';
import {
    Table,
    Tag,
    Space,
    Select,
    Button,
    Typography,
    Card,
    Row,
    Col,
    Input,
    Spin,
    message,
} from 'antd';
import {
    SearchOutlined,
    ReloadOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import { listPatrols } from '../../../config/services/patrolService';
import { parseApiError } from '../../../config/services/errorHandler';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';

const { Text } = Typography;
const { Option } = Select;

const PATROL_STATUS_COLORS = {
    active: 'blue',
    completed: 'green',
    cancelled: 'red',
};

/**
 * PatrolsList — Company-wide patrol list with filters and pagination.
 * Filters are preserved in the URL query string.
 */
const PatrolsList = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [filters, setFilters] = useState({
        propertyId: searchParams.get('propertyId') ?? '',
        guardId: searchParams.get('guardId') ?? '',
        status: searchParams.get('status') ?? '',
        page: Number(searchParams.get('page') ?? 1),
        perPage: 20,
    });

    // Sync filters → URL
    useEffect(() => {
        const params = {};
        if (filters.propertyId) params.propertyId = filters.propertyId;
        if (filters.guardId) params.guardId = filters.guardId;
        if (filters.status) params.status = filters.status;
        if (filters.page > 1) params.page = filters.page;
        setSearchParams(params, { replace: true });
    }, [filters]);

    const { data, isLoading, isError, refetch } = useQuery(
        ['patrols', filters],
        () => listPatrols(filters),
        {
            keepPreviousData: true,
            onError: (err) => {
                const parsed = parseApiError(err);
                message.error(parsed.message);
            },
        }
    );

    const rows = data?.rows ?? [];
    const total = data?.total ?? 0;

    const updateFilter = (key, val) => {
        setFilters((prev) => ({ ...prev, [key]: val, page: 1 }));
    };

    const columns = [
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            width: 110,
            render: (s) => (
                <Tag color={PATROL_STATUS_COLORS[s] ?? 'default'}>{s?.toUpperCase()}</Tag>
            ),
        },
        {
            title: 'Property',
            key: 'property',
            render: (_, r) => <Text>{r.property?.name ?? '—'}</Text>,
        },
        {
            title: 'Guard',
            key: 'guard',
            render: (_, r) => <Text>{r.guard?.name ?? '—'}</Text>,
        },
        {
            title: 'Started',
            dataIndex: 'startedAt',
            key: 'startedAt',
            render: (t) => t ? dayjs(t).format('DD MMM YYYY, HH:mm') : '—',
            sorter: false,
        },
        {
            title: 'Completed',
            dataIndex: 'completedAt',
            key: 'completedAt',
            render: (t) => t ? dayjs(t).format('DD MMM YYYY, HH:mm') : <Tag color="blue">In Progress</Tag>,
        },
        {
            title: 'Round',
            dataIndex: 'roundNumber',
            key: 'roundNumber',
            width: 80,
            align: 'center',
        },
        {
            title: 'Scans',
            dataIndex: 'scanCount',
            key: 'scanCount',
            width: 70,
            align: 'center',
        },
        {
            title: 'Missed',
            dataIndex: 'missedCount',
            key: 'missedCount',
            width: 80,
            align: 'center',
            render: (n) => (
                <Text style={{ color: n > 0 ? '#ef4444' : 'inherit', fontWeight: n > 0 ? 600 : 400 }}>
                    {n}
                </Text>
            ),
        },
        {
            title: '',
            key: 'action',
            width: 60,
            align: 'center',
            render: (_, r) => (
                <Button
                    size="small"
                    icon={<EyeOutlined />}
                    onClick={() => navigate(`/patrols/${r.id}`)}
                    title="View Detail"
                />
            ),
        },
    ];

    return (
        <div>
            <div style={{ marginBottom: 20 }}>
                <Text style={{ fontSize: 20, fontWeight: 700 }}>Patrols</Text>
                <Text type="secondary" style={{ marginLeft: 10, fontSize: 13 }}>
                    Company-wide patrol activity
                </Text>
            </div>

            {/* Filters */}
            <Card size="small" style={{ marginBottom: 16, borderRadius: 10 }}>
                <Row gutter={[12, 12]} align="middle">
                    <Col xs={24} sm={8} md={6}>
                        <Input
                            placeholder="Property ID"
                            prefix={<SearchOutlined />}
                            value={filters.propertyId}
                            onChange={(e) => updateFilter('propertyId', e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                        <Input
                            placeholder="Guard ID"
                            prefix={<SearchOutlined />}
                            value={filters.guardId}
                            onChange={(e) => updateFilter('guardId', e.target.value)}
                            allowClear
                        />
                    </Col>
                    <Col xs={24} sm={8} md={6}>
                        <Select
                            style={{ width: '100%' }}
                            placeholder="Status"
                            allowClear
                            value={filters.status || undefined}
                            onChange={(val) => updateFilter('status', val ?? '')}
                        >
                            <Option value="active">Active</Option>
                            <Option value="completed">Completed</Option>
                            <Option value="cancelled">Cancelled</Option>
                        </Select>
                    </Col>
                    <Col xs={24} sm={24} md={6} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={refetch}
                            style={{
                                height: '40px',
                                borderRadius: '8px',
                                fontWeight: 500,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: '6px',
                                borderColor: '#d9d9d9',
                                boxShadow: 'none',
                            }}
                        >
                            Refresh
                        </Button>
                    </Col>
                </Row>
            </Card>

            <Spin spinning={isLoading}>
                <Table
                    dataSource={rows}
                    columns={columns}
                    rowKey="id"
                    size="small"
                    scroll={{ x: 800 }}
                    onRow={(r) => ({
                        onClick: () => navigate(`/patrols/${r.id}`),
                        style: { cursor: 'pointer' },
                    })}
                    pagination={{
                        current: filters.page,
                        pageSize: filters.perPage,
                        total,
                        showSizeChanger: false,
                        showTotal: (t) => `${t} patrols`,
                        onChange: (page) => setFilters((prev) => ({ ...prev, page })),
                    }}
                />
            </Spin>

            {isError && (
                <Text type="danger" style={{ display: 'block', marginTop: 8, textAlign: 'center' }}>
                    Failed to load patrols. <Button type="link" onClick={refetch}>Retry</Button>
                </Text>
            )}
        </div>
    );
};

export default PatrolsList;
